import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { publish, SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { DesignMode, DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { useFeatureFlag } from '../../../../../../hooks/use-feature-flag';
import { CollapsableSection } from '../../../components/collapsable-section';
import { OptionLabels } from '../../../components/option-labels';

type Props = {
  expanded: boolean;
  tool: DesignTool;
};

export const DesignModeSection = (props: Props) => {
  const { t } = useTranslation();
  const designCurves = useFeatureFlag('SK-7535-design-curves');
  const [tool, setTool] = React.useState(props.tool.designMode);

  React.useEffect(() => {
    props.tool.onDesignModeChanged = (tool) => {
      setTool(tool);
    };

    return () => {
      props.tool.onDesignModeChanged = undefined;
    };
  });

  return (
    <CollapsableSection
      expanded={props.expanded}
      title={t('section.tools.title', { ns: 'skyviewDesign' })}
      titleUpperCase={true}
      onSetExpanded={(state) =>
        publish(SubscriptionTopic.DesignToolMenuStateChanged, { designMode: state })
      }
    >
      <OptionLabels
        active={tool}
        itemRows={[
          [
            {
              title: t('section.tools.lineButton.text', { ns: 'skyviewDesign' }),
              value: DesignMode.Line,
              onSelect: () => (props.tool.designMode = DesignMode.Line),
              tooltipText: (
                <Trans
                  components={{ bold: <strong />, br: <br /> }}
                  i18nKey="section.tools.lineButton.tooltip"
                  ns="skyviewDesign"
                />
              ),
            },
            {
              title: t('section.tools.pointButton.text', { ns: 'skyviewDesign' }),
              value: DesignMode.Point,
              onSelect: () => (props.tool.designMode = DesignMode.Point),
              tooltipText: (
                <Trans
                  components={{ bold: <strong />, br: <br /> }}
                  i18nKey="section.tools.pointButton.tooltip"
                  ns="skyviewDesign"
                />
              ),
            },
          ],
          [
            {
              title: t('section.tools.selectButton.text', { ns: 'skyviewDesign' }),
              value: DesignMode.Select,
              onSelect: () => (props.tool.designMode = DesignMode.Select),
              tooltipText: (
                <Trans
                  components={{ bold: <strong />, br: <br /> }}
                  i18nKey="section.tools.selectButton.tooltip"
                  ns="skyviewDesign"
                />
              ),
            },
            {
              title: t('section.tools.textButton.text', { ns: 'skyviewDesign' }),
              value: DesignMode.Text,
              onSelect: () => (props.tool.designMode = DesignMode.Text),
              tooltipText: (
                <Trans
                  components={{ bold: <strong />, br: <br /> }}
                  i18nKey="section.tools.textButton.tooltip"
                  ns="skyviewDesign"
                />
              ),
            },
          ],
          ...(designCurves
            ? [
                [
                  {
                    title: t('section.tools.curveButton.text', { ns: 'skyviewDesign' }),
                    value: DesignMode.Curve,
                    onSelect: () => (props.tool.designMode = DesignMode.Curve),
                    tooltipText: (
                      <Trans
                        components={{ bold: <strong />, br: <br /> }}
                        i18nKey="section.tools.curveButton.tooltip"
                        ns="skyviewDesign"
                      />
                    ),
                  },
                  {
                    title: t('section.tools.circleButton.text', { ns: 'skyviewDesign' }),
                    value: DesignMode.Circle,
                    onSelect: () => (props.tool.designMode = DesignMode.Circle),
                    tooltipText: (
                      <Trans
                        components={{ bold: <strong />, br: <br /> }}
                        i18nKey="section.tools.circleButton.tooltip"
                        ns="skyviewDesign"
                      />
                    ),
                  },
                ],
              ]
            : []),
        ]}
      />
    </CollapsableSection>
  );
};
